/**
 * Mise en forme des annonces saisies dans l'admin.
 *
 * Le texte était affiché tel quel : les lignes vides disparaissaient, les
 * `<br>` et les `**gras**` s'affichaient en toutes lettres. On accepte donc
 * une mise en forme minimale, et on la rend en NŒUDS React — jamais en HTML
 * injecté : le bandeau est en page d'accueil publique, et un texte d'admin
 * n'a pas à pouvoir y poser du script.
 *
 * Règles, dans cet ordre :
 *   - `<br>` (ou `<br/>`, `<br />`) vaut un retour à la ligne : Joseph en a
 *     déjà tapé, ils doivent continuer à marcher ;
 *   - une ligne vide ouvre un nouveau paragraphe ;
 *   - un simple retour à la ligne fait un saut de ligne ;
 *   - `**texte**` met en gras.
 */

export type Fragment = { texte: string; gras: boolean }
export type Ligne = Fragment[]
export type Paragraphe = Ligne[]

// Une SUITE de <br>, avec ce qui la colle : le retour à la ligne que l'on tape
// juste après un <br> est de la frappe, pas un saut de plus. Sans ça,
// « …compréhension.<br> » suivi d'une nouvelle ligne valait deux sauts et
// séparait la signature en paragraphe.
const BALISE_BR = /[ \t]*(?:<br\s*\/?>[ \t]*)+\n?/gi
const GRAS = /\*\*(.+?)\*\*/g

/** Découpe une ligne en fragments gras / non gras. `**` orphelin = littéral. */
export function fragments(ligne: string): Ligne {
  const out: Fragment[] = []
  let curseur = 0
  for (const m of ligne.matchAll(GRAS)) {
    if (m.index > curseur) out.push({ texte: ligne.slice(curseur, m.index), gras: false })
    out.push({ texte: m[1], gras: true })
    curseur = m.index + m[0].length
  }
  if (curseur < ligne.length) out.push({ texte: ligne.slice(curseur), gras: false })
  return out.length ? out : [{ texte: '', gras: false }]
}

export function analyserTexte(texte: string): Paragraphe[] {
  const normalise = (texte || '')
    .replace(/\r\n?/g, '\n')
    .replace(BALISE_BR, (m) => '\n'.repeat((m.match(/<br/gi) || []).length))
  return normalise
    .split(/\n[ \t]*\n+/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0)
    .map((p) => p.split('\n').map((l) => fragments(l.trim())))
}
